import { css } from "@emotion/css";
import { Button } from "@octopusdeploy/design-system-components";
import { ThumbsUpIcon, ThumbsDownIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens, borderRadius, borderWidth, fontWeight } from "@octopusdeploy/design-system-tokens";
import { tracers, TraceFlags } from "@octopusdeploy/telemetry";
import React, { useState } from "react";
interface RecoveryAgentFeedbackProps {
    traceId: string;
    spanId: string;
}
const MAX_FEEDBACK_LENGTH = 1024;
export const RecoveryAgentFeedback = (props: RecoveryAgentFeedbackProps) => {
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [feedbackType, setFeedbackType] = useState<"Positive" | "Negative">("Positive");
    const [feedbackReason, setFeedbackReason] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handlePositiveFeedback = () => {
        setFeedbackType("Positive");
        setShowFeedbackForm(true);
    };
    const handleNegativeFeedback = () => {
        setFeedbackType("Negative");
        setShowFeedbackForm(true);
    };
    const handleFeedbackSubmit = () => {
        setIsSubmitting(true);
        const reason = feedbackReason.trim();
        const span = tracers.default.startSpan("Recovery Agent Feedback Submitted", {
            links: [
                {
                    context: {
                        traceId: props.traceId,
                        spanId: props.spanId,
                        traceFlags: TraceFlags.SAMPLED,
                    },
                },
            ],
        });
        span.setAttribute("feedback.type", feedbackType);
        span.setAttribute("feedback.has_text", !!reason);
        if (reason) {
            span.setAttribute("feedback.reason", reason);
        }
        span.end();
        setFeedbackSubmitted(true);
        setIsSubmitting(false);
    };
    if (feedbackSubmitted) {
        return (<div className={styles.feedbackContainer}>
                <p className={styles.thankYouMessage}>Thanks for your feedback!</p>
            </div>);
    }
    return (<div className={styles.feedbackContainer}>
            {showFeedbackForm ? (<div className={styles.feedbackForm}>
                    <div className={styles.textareaContainer}>
                        <label htmlFor="feedback-reason" className={styles.textareaLabel}>
                            Feedback (optional):
                        </label>
                        <textarea id="feedback-reason" className={styles.textarea} value={feedbackReason} onChange={(e) => setFeedbackReason(e.target.value)} placeholder="Share your feedback..." rows={3} maxLength={MAX_FEEDBACK_LENGTH}/>
                        <div className={styles.characterCounter}>
                            {feedbackReason.length} / {MAX_FEEDBACK_LENGTH} characters
                        </div>
                    </div>
                    <div className={styles.formActions}>
                        <Button label="Submit" importance="primary" onClick={handleFeedbackSubmit} disabled={isSubmitting}/>
                        <Button label="Cancel" importance="ghost" onClick={() => {
                setShowFeedbackForm(false);
                setFeedbackReason("");
            }}/>
                    </div>
                </div>) : (<div className={styles.buttonContainer}>
                    <span className={styles.feedbackPrompt}>Rate the response</span>
                    <div className={styles.buttons}>
                        <Button icon={<ThumbsDownIcon size={16}/>} size="small" importance="quiet" onClick={handleNegativeFeedback} aria-label="Negative feedback"/>
                        <Button icon={<ThumbsUpIcon size={16}/>} size="small" importance="quiet" onClick={handlePositiveFeedback} aria-label="Positive feedback"/>
                    </div>
                </div>)}
        </div>);
};
const styles = {
    feedbackContainer: css({
        marginTop: space[8],
        paddingTop: space[8],
    }),
    buttonContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[4],
        padding: space[8],
        backgroundColor: themeTokens.color.background.info,
        width: "fit-content",
    }),
    feedbackPrompt: css({
        margin: 0,
        font: text.body.regular.xSmall,
        fontWeight: fontWeight["700"],
        color: themeTokens.color.text.secondary,
        display: "flex",
        alignItems: "center",
    }),
    buttons: css({
        display: "flex",
        gap: space[4],
    }),
    feedbackForm: css({
        display: "flex",
        flexDirection: "column",
        gap: space[12],
    }),
    textareaContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[4],
    }),
    textareaLabel: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.primary,
    }),
    characterCounter: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.tertiary,
        textAlign: "right",
    }),
    textarea: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.primary,
        backgroundColor: themeTokens.color.background.primary.default,
        border: `${borderWidth["1"]} solid ${themeTokens.color.border.primary}`,
        borderRadius: borderRadius.medium,
        padding: space[8],
        resize: "vertical",
        minHeight: "80px",
        "&:focus": {
            outline: "none",
            borderColor: themeTokens.color.textField.border.active,
        },
        "&::placeholder": {
            color: themeTokens.color.text.tertiary,
        },
    }),
    formActions: css({
        display: "flex",
        gap: space[8],
    }),
    thankYouMessage: css({
        margin: 0,
        font: text.body.regular.medium,
    }),
};
